import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createIcebergPayment,
  getIcebergPaymentStatus,
} from '../../../api/requests/petitions';

export function useIcebergPayment(onSuccess?: () => void) {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPayment = useCallback(
    async (petitionId: number) => {
      setLoading(true);
      setError(null);
      setPaymentStatus(null);

      const result = await createIcebergPayment(petitionId);
      if (result.error) {
        setLoading(false);
        setError('Error creating payment');
        return;
      }

      // Start polling every 5 seconds
      intervalRef.current = setInterval(async () => {
        const statusRes = await getIcebergPaymentStatus(petitionId);
        if (!statusRes.error && statusRes.data) {
          const status = (statusRes.data as { status: string }).status;
          if (status === 'success' || status === 'failed') {
            stopPolling();
            setPaymentStatus(status);
            setLoading(false);
            if (status === 'success' && onSuccess) {
              onSuccess();
            }
          }
        }
      }, 5000);
    },
    [stopPolling, onSuccess],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { startPayment, stopPolling, loading, paymentStatus, error };
}
